<script setup lang="ts">
import {
  formatMoney,
  formattedPricePerUnit,
  discountPercentage
} from "~/utils/formatters"
import { SingleProps } from "./Single.props"
import { productUtils } from "~/utils/product"
import { customPaths } from "~/utils/constants"
import { useStorage } from "@vueuse/core"
import { sendAlgoliaAddToCartEvent } from "~/utils/algolia"
import { algoliaSearch } from "~~/config/algolia"
import { SaveForLaterProductForAction } from "~/composables/useSavedForLater"
import { getSkuItemMetadata } from "~/utils/savedForLater/savedForLater"
import { cleanObject } from "~/utils/analytics/analytics"
import { getAlgoliaAddToCartForInsights } from "~/utils/algolia/algolia-events-payload"
import type { MultipackPickerProps } from "~/components/Utils/MultipackPicker.props"

const props = withDefaults(defineProps<SingleProps>(), {
  pricePerUnit: 0,
  unitType: "",
  isSopOtc: false
})

// Sticky bottom bar visibility
const purchaseBlockRef = ref<HTMLElement | null>(null)
const showStickyBar = ref(false)

onMounted(() => {
  if (!purchaseBlockRef.value) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        showStickyBar.value = !entry.isIntersecting
      })
    },
    {
      threshold: 0,
      rootMargin: "0px"
    }
  )

  observer.observe(purchaseBlockRef.value)

  onUnmounted(() => {
    observer.disconnect()
  })
})

// Discount percentage
const discount = computed(() =>
  discountPercentage(props.price, props.product.details.oldPrice ?? 0)
)

// Sticky bar image
const stickyBarImage = computed(() => {
  const firstGalleryItem = props.product.details.gallery?.[0]
  if (firstGalleryItem && "image" in firstGalleryItem) {
    return firstGalleryItem.image
  }
  return undefined
})

const isAvailableAtLocalWareHouse = computed(
  () => quantity.value < (props.product.details.localWarehouseStock ?? 0)
)

const quantity = ref(1)
const isLoading = ref(false)
const changeQuantityClicked = ref(false)

const packageNumber = computed(() => {
  const currentMultipackOption = props.multipackOptions?.find(
    (option) => option.current
  )
  const actualQuantity = currentMultipackOption
    ? quantity.value * currentMultipackOption.quantity
    : quantity.value
  return `(${actualQuantity} confezion${
    actualQuantity === 1 ? "e" : "i"
  } ${formatMoney(props.price * quantity.value)})`
})

const emit =
  defineEmits<{
    (e: "onPurchase", value: number, isSticky: boolean): void
    (e: "onChangeQuantity", value: number): void
    (e: "onSaveForLater"): void
  }>()

const algoliaLastClickedProduct = useStorage("algolia-last-clicked-product", {
  queryID: "",
  path: ""
})

const handleChangeQuantity = (newQuantity: number) => {
  quantity.value = newQuantity
  emit("onChangeQuantity", newQuantity)
}

const handleChangeQuantityClicked = (direction: string) => {
  changeQuantityClicked.value = true
}

const trackAlgoliaPdpAddToCart = () => {
  try {
    const route = useRoute()
    const algolia = algoliaSearch()
    const algoliaLastClickedProductValue = algoliaLastClickedProduct.value

    if (
      algoliaLastClickedProductValue &&
      route.path == algoliaLastClickedProductValue.path &&
      algoliaLastClickedProductValue.queryID
    ) {
      const productItemForInsights = getAlgoliaAddToCartForInsights(
        props.product.details.price,
        props.product.details.productId,
        quantity.value,
        props.product.details.oldPrice
      )

      sendAlgoliaAddToCartEvent(
        productItemForInsights,
        algolia.indices.products,
        algoliaLastClickedProduct.value?.queryID
      )
    }
  } catch (error) {
    console.error("PDP Algolia add-to-cart tracking failed", error)
  }
}

const handlePurchase = (value: number, isSticky = false) => {
  isLoading.value = true
  emit("onPurchase", value, isSticky)
  trackAlgoliaPdpAddToCart()
  setTimeout(() => {
    // TODO: IT'S POSSIBILE TO MANAGE THIS ISLOADING VARIABLE LIKE A PROP FROM THE PARENT PAGE
    isLoading.value = false
  }, 1000)
}

const handlePurchaseThrottled = useThrottleFn(handlePurchase, 1000)
const handleMultipackSwitch = (slug: string) => navigateTo(slug)

const product = computed((): SaveForLaterProductForAction => {
  return {
    productId: props.product.details.productId,
    skuId: props.product.details.skuId,
    metadata: getSkuItemMetadata(
      props.product.details?.gallery[0],
      props.product.details.slug,
      props.product.details.price,
      props.product.details.oldPrice,
      props.product.details.stockQuantity,
      props.product.description?.infos?.unitOfMeasure,
      props.product.details.expirationDate,
      props.product.details.isDeductable,
      cleanObject(props.product.gaItem),
      (props.multipackOptions.find((o) => o.current)?.quantity ?? 1) > 1
    )
  }
})

const multipackProps = computed((): MultipackPickerProps => {
  return {
    multipackOptions:
      props.multipackOptions?.map((option) => ({
        current: option.current,
        oldPrice: option.oldPrice,
        price: option.price,
        quantity: option.quantity,
        slug: option.slug
      })) ?? []
  }
})

const hasOtherMultipackOptions = computed((): boolean =>
  multipackProps.value.multipackOptions.some((option) => !option.current)
)

const pricePerPackage = computed((): number => {
  const currentMultipackOption = props.multipackOptions?.find(
    (option) => option.current
  )
  return currentMultipackOption
    ? currentMultipackOption.price / (currentMultipackOption.quantity * 1.0)
    : props.price
})
</script>

<template>
  <div
    ref="purchaseBlockRef"
    class="
      single-purchase-type-wrapper
      flex flex-col
      gap-4
      rounded-lg
      p-4
      shadow-01
    "
  >
    <UtilsMarkdown
      v-if="hasOtherMultipackOptions"
      class="beaver text-black-80"
      :content="
        $t(
          isSopOtc
            ? 'productPage.purchaseType.organism.choosePurchaseOption'
            : 'productPage.purchaseType.organism.buyMoreSpendLess'
        )
      "
    />
    <UtilsMultipackPicker
      v-if="hasOtherMultipackOptions"
      v-bind="multipackProps"
      @click="handleMultipackSwitch"
    />
    <!-- Availability -->
    <div>
      <p class="mb-0 flex w-full items-center">
        <UtilsIcon
          name="CheckmarkFull.svg"
          color="positive-500"
          class="inline-block h-6 w-6"
          fetch-priority="high"
          :preload="true"
        />
        <span class="ms-2">
          <UtilsMarkdown
            :content="$t('productPage.detail.storeLocation.localWareHouse')"
            tag="span"
            class="beaver md:pig"
            v-if="isAvailableAtLocalWareHouse"
          />
          <UtilsMarkdown
            :content="$t('productPage.detail.storeLocation.dropshipping')"
            tag="span"
            class="beaver md:pig"
            v-else
          />
        </span>
      </p>
      <p class="mouse md:beaver mt-1">
        <span>
          {{ $t("productPage.detail.storeLocationSingleDetails.heading") }}
        </span>
        <span
          class="mouse-bold md:beaver-bold text-positive-500 ms-1"
          v-if="isAvailableAtLocalWareHouse"
        >
          {{
            $t("productPage.detail.storeLocationSingleDetails.localWareHouse")
          }}
        </span>
        <span class="mouse-bold md:beaver-bold text-orange-300 ms-1" v-else>
          {{
            $t("productPage.detail.storeLocationSingleDetails.dropshipping")
          }} </span
        >.
        <AppLink
          :to="customPaths.shippings"
          class="
            mouse-medium
            md:beaver-medium
            text-green-main
            hover:text-green-300
            cursor-pointer
            underline
          "
        >
          {{ $t("productPage.detail.storeLocationSingleDetails.learnMore") }}
        </AppLink>
      </p>
    </div>

    <UtilsQuantityPicker
      :quantity="quantity"
      :min="1"
      :max="stockQuantity ?? 1"
      @update:modelValue="handleChangeQuantity"
      @click="handleChangeQuantityClicked"
    />

    <p
      class="mouse md:beaver text-negative-main"
      v-if="changeQuantityClicked && quantity >= (stockQuantity ?? 0)"
    >
      {{
        $t(
          "productPage.purchaseType.organism.errorMessages.maxQuantity.inStock"
        )
      }}
    </p>

    <p class="beaver text-black-80 flex flex-wrap items-center gap-1">
      {{ $t("productPage.purchaseType.organism.costPerPackage") }}
      <span class="beaver-bold">{{ formatMoney(pricePerPackage) }}</span>
      <template
        v-if="productUtils.isDisplayablePricePerUnit(pricePerUnit, unitType)"
      >
        <span class="mouse">
          {{ formattedPricePerUnit(formatMoney(pricePerUnit), unitType) }}
        </span>
      </template>
    </p>

    <div class="flex items-stretch gap-2">
      <UtilsButton
        @click="handlePurchaseThrottled(quantity, false)"
        :fluid="true"
        :isInnerFullHeight="true"
      >
        <template #icon>
          <div class="grid place-items-center">
            <p
              class="flex flex-wrap justify-center gap-1 [grid-area:1/1]"
              :class="{ invisible: isLoading }"
            >
              <span>{{
                $t("productPage.purchaseType.organism.addToCart")
              }}</span>
              <span>{{ packageNumber }}</span>
            </p>
            <UtilsLoader
              v-if="isLoading"
              class="justify-center [grid-area:1/1]"
            />
          </div>
        </template>
      </UtilsButton>

      <ProductSavedForLaterAction
        :product="product"
        v-if="!!isSaveForLaterEnabled"
        @onSaveForLater="emit('onSaveForLater')"
      ></ProductSavedForLaterAction>
    </div>
  </div>

  <!-- Sticky Bottom Bar -->
  <ProductPagePurchaseTypeStickyBar
    :show="showStickyBar"
    :title="props.product.details.title"
    :price="price"
    :oldPrice="props.product.details.oldPrice"
    :discount="discount"
    :image="stickyBarImage"
    :quantity="quantity"
    :maxQuantity="stockQuantity ?? 1"
    :isLoading="isLoading"
    @update:quantity="handleChangeQuantity"
    @addToCart="handlePurchaseThrottled(quantity, true)"
  />
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
